import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '../services/api';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Search,
  Users,
  Phone,
  Mail,
  MapPin,
  Edit,
  Trash2,
  Loader2,
  UserCheck,
  UserX,
  Building,
  Camera,
  User,
  Clock,
  History,
  Send,
  CheckCircle,
  XCircle,
  RefreshCw,
  Wallet,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import AdvancesDialog from '../components/AdvancesDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Avatar component with initials fallback
const UserAvatar = ({ user, size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-16 h-16 text-xl'
  };
  
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };
  
  const getRoleColor = (role) => {
    switch(role) {
      case 'admin': return 'bg-gradient-to-br from-purple-500 to-purple-700';
      case 'technicien': return 'bg-gradient-to-br from-blue-500 to-blue-700';
      case 'housekeeper': return 'bg-gradient-to-br from-emerald-500 to-emerald-700';
      default: return 'bg-gradient-to-br from-gray-500 to-gray-700';
    }
  };

  // Construire l'URL de l'avatar (supporte Cloudinary et local)
  const getAvatarUrl = (avatarUrl) => {
    if (!avatarUrl) return null;
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      return avatarUrl;
    }
    return `${API_URL}/api/${avatarUrl}`;
  };

  if (user.avatar_url) {
    const imgSrc = getAvatarUrl(user.avatar_url);
    return (
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden ring-2 ring-white shadow-md flex-shrink-0 ${className}`}>
        <img 
          src={imgSrc} 
          alt={user.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className={`w-full h-full ${getRoleColor(user.role)} items-center justify-center text-white font-semibold hidden`}>
          {getInitials(user.name)}
        </div>
      </div>
    );
  }
  
  return (
    <div className={`${sizeClasses[size]} rounded-full ${getRoleColor(user.role)} flex items-center justify-center text-white font-semibold ring-2 ring-white shadow-md flex-shrink-0 ${className}`}>
      {getInitials(user.name)}
    </div>
  );
};

const UsersManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showEmailLogsDialog, setShowEmailLogsDialog] = useState(false);
  const [showAdvancesDialog, setShowAdvancesDialog] = useState(false);
  const [advancesTargetUser, setAdvancesTargetUser] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(null);
  const [loginHistory, setLoginHistory] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'housekeeper',
    sector: '',
    siret: '',
    company_name: '',
    company_address: '',
    is_also_housekeeper: false,
    allow_late_photos: false,
    require_gps_photos: false,
    email_notifications_enabled: true,
    access_restricted: false,
    does_not_do_linen: false,
    linen_personal_percentage: 30,
    enforce_time_billing: false,
    skip_keybox_photo: false,
    allow_foreign_access: false
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const [recalculating, setRecalculating] = useState(false);
  const [adjustmentHistory, setAdjustmentHistory] = useState(null);
  const [loadingAdjustments, setLoadingAdjustments] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);

  const runDiagnostic = async () => {
    if (!editingUser?.id) return;
    setShowDiagnostic(true);
    setDiagnostic({ loading: true });
    try {
      const res = await fetch(`${API_URL}/api/users/${editingUser.id}/time-billing-diagnose`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur diagnostic');
      setDiagnostic(data);
    } catch (e) {
      setDiagnostic({ error: e.message });
    }
  };

  // Load time-billing adjustments history for the FDM being edited
  const loadAdjustments = useCallback(async (userId) => {
    if (!userId) return;
    setLoadingAdjustments(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}/time-billing-adjustments`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setAdjustmentHistory(await res.json());
      }
    } catch (e) {
      console.error('load adjustments failed', e);
    } finally {
      setLoadingAdjustments(false);
    }
  }, []);

  // Bulk recalc button — recompute today's tasks for this FDM
  const handleRecalculateTimeBilling = async () => {
    if (!editingUser?.id) return;
    if (!window.confirm(
      `Recalculer les missions terminées AUJOURD'HUI de ${editingUser.name} au tarif horaire réel (32€/h) ?\n\n` +
      `Les prix des prestations terminées en avance seront ajustés automatiquement.`
    )) return;
    setRecalculating(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/recalculate-time-billing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ user_id: editingUser.id })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Erreur');
      }
      if (data.adjusted === 0) {
        toast.success(`Aucune mission à ajuster (${data.scanned} missions vérifiées aujourd'hui).`);
      } else {
        toast.success(
          `✅ ${data.adjusted} mission(s) ajustée(s) — ${data.total_delta_eur}€ économisés`,
          { duration: 8000 }
        );
        // Notify other pages (Payments, FDM earnings, etc.) to refetch their totals
        try { window.dispatchEvent(new CustomEvent('mcb-tasks-updated', { detail: data })); } catch (e) {}
      }
      loadAdjustments(editingUser.id);
    } catch (e) {
      toast.error(e.message || 'Erreur lors du recalcul');
    } finally {
      setRecalculating(false);
    }
  };

  // Format relative time
  const formatLastLogin = (dateStr) => {
    if (!dateStr) return 'Jamais';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  // Fetch login history
  const fetchLoginHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/users/login-history?days=7`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLoginHistory(data);
        setShowHistoryDialog(true);
      }
    } catch (error) {
      console.error('Failed to fetch login history:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    }
  };

  // Fetch email logs
  const fetchEmailLogs = async () => {
    setEmailLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/email-logs?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmailLogs(data.logs || []);
        setShowEmailLogsDialog(true);
      }
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setEmailLogsLoading(false);
    }
  };

  // Send test email to FDM
  const sendTestEmailToFDM = async (userId, userName, userEmail) => {
    setSendingTestEmail(userId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/send-test-email-to-fdm/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Email de test envoyé à ${userName} (${userEmail})`);
        // Refresh logs
        fetchEmailLogs();
      } else {
        const error = await res.json();
        toast.error(`Erreur: ${error.detail || 'Échec de l\'envoi'}`);
      }
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingTestEmail(null);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch (error) {
      console.error('Users error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSubmit = async () => {
    if (!formData.email || !formData.name || (!editingUser && !formData.password)) {
      toast.error('Remplissez les champs obligatoires');
      return;
    }
    setActionLoading(true);
    try {
      if (editingUser) {
        await usersApi.update(editingUser.id, formData);
        toast.success('Utilisateur modifié');
      } else {
        await usersApi.create(formData);
        toast.success('Utilisateur créé');
      }
      setShowDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archiver cet utilisateur ?\n\nSes données seront conservées et vous pourrez le restaurer à tout moment via le filtre "Voir archivés".')) return;
    try {
      await usersApi.delete(id);
      toast.success('Utilisateur archivé — restaurable à tout moment');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'archivage');
    }
  };

  const handleRestore = async (user) => {
    if (!window.confirm(`Restaurer ${user.name} ?\n\nLe compte redeviendra actif et la personne pourra se reconnecter avec ses anciens identifiants.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/users/${user.id}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(data.message || 'Utilisateur restauré');
      fetchUsers();
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la restauration');
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', name: '', phone: '', role: 'housekeeper', sector: '', siret: '', company_name: '', company_address: '', is_also_housekeeper: false, allow_late_photos: false, require_gps_photos: false, email_notifications_enabled: true, access_restricted: false, does_not_do_linen: false, linen_personal_percentage: 30, enforce_time_billing: false, skip_keybox_photo: false, allow_foreign_access: false });
    setEditingUser(null);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingUser) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }
    
    setUploadingAvatar(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(`${API_URL}/api/users/${editingUser.id}/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataUpload
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const result = await response.json();
      
      // Update the editing user with new avatar
      setEditingUser(prev => ({ ...prev, avatar_url: result.avatar_url }));
      
      toast.success('Photo de profil mise à jour');
      fetchUsers();
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error('Erreur lors de l\'upload de la photo');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      phone: user.phone || '',
      role: user.role,
      sector: user.sector || '',
      siret: user.siret || '',
      company_name: user.company_name || '',
      company_address: user.company_address || '',
      is_also_housekeeper: user.is_also_housekeeper || false,
      allow_late_photos: user.allow_late_photos || false,
      require_gps_photos: user.require_gps_photos || false,
      email_notifications_enabled: user.email_notifications_enabled !== false,
      access_restricted: user.access_restricted || false,
      does_not_do_linen: user.does_not_do_linen || false,
      linen_personal_percentage: user.linen_personal_percentage != null ? user.linen_personal_percentage : 30,
      enforce_time_billing: user.enforce_time_billing || false,
      skip_keybox_photo: user.skip_keybox_photo || false,
      allow_foreign_access: user.allow_foreign_access || false
    });
    setShowDialog(true);
    // Load time-billing adjustments history for admin view
    setAdjustmentHistory(null);
    if ((user.role === 'housekeeper' || user.is_also_housekeeper) && user.id) {
      loadAdjustments(user.id);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-700',
      technicien: 'bg-blue-100 text-blue-700',
      housekeeper: 'bg-emerald-100 text-emerald-700',
      gouvernant: 'bg-amber-100 text-amber-700'
    };
    const labels = {
      admin: 'Admin',
      technicien: 'Technicien',
      housekeeper: 'Femme de ménage',
      gouvernant: 'Gouvernant'
    };
    return <Badge className={`${styles[role] || 'bg-gray-100'} border-0`}>{labels[role] || role}</Badge>;
  };

  const [showArchived, setShowArchived] = useState(false);

  const filteredUsers = users.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (searchQuery && !u.name.toLowerCase().includes(searchQuery.toLowerCase()) && !u.email.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    // Filtre archivés (is_active=false)
    if (showArchived) {
      if (u.is_active !== false) return false;  // Only inactive
    } else {
      if (u.is_active === false) return false;  // Only active
    }
    return true;
  });

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 overflow-auto ml-0 md:ml-72">
        <div className="p-4 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <h1 className="text-2xl font-bold text-[#003366]">
              Équipes {showArchived && <span className="text-slate-500 text-lg font-normal">· Archivés</span>}
            </h1>
            <div className="flex gap-3 mt-4 md:mt-0 flex-wrap">
              <Button
                variant={showArchived ? "default" : "outline"}
                onClick={() => setShowArchived(!showArchived)}
                className={showArchived ? "bg-slate-700 hover:bg-slate-800 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-100"}
                data-testid="toggle-archived-btn"
              >
                📁 {showArchived ? 'Voir les actifs' : `Voir les archivés (${users.filter(u => u.is_active === false).length})`}
              </Button>
              <Button 
                variant="outline"
                onClick={fetchEmailLogs}
                disabled={emailLogsLoading}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="view-email-logs-btn"
              >
                {emailLogsLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Emails envoyés
              </Button>
              <Button 
                variant="outline"
                onClick={fetchLoginHistory}
                className="border-slate-300"
                data-testid="view-history-btn"
              >
                <History className="h-4 w-4 mr-2" />
                Historique 7j
              </Button>
              <Button 
                onClick={() => { resetForm(); setShowDialog(true); }}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="add-user-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouvel utilisateur
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-users"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-[200px]" data-testid="filter-role">
                <SelectValue placeholder="Filtrer par rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="technicien">Technicien</SelectItem>
                <SelectItem value="housekeeper">Femme de ménage</SelectItem>
                <SelectItem value="gouvernant">Gouvernant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#003366]" />
            </div>
          ) : (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Membre</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Téléphone</TableHead>
                        <TableHead>Rôle</TableHead>
                        <TableHead>Dernière connexion</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            Aucun utilisateur trouvé
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredUsers.map((user) => (
                          <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <UserAvatar user={user} size="md" />
                                <span className="font-medium">{user.name}</span>
                              </div>
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.phone || '-'}</TableCell>
                            <TableCell>{getRoleBadge(user.role)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                <Clock className="h-3.5 w-3.5 text-slate-400" />
                                {formatLastLogin(user.last_login)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap items-center gap-1.5">
                                {user.is_active !== false ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-0">
                                    <UserCheck className="h-3 w-3 mr-1" />
                                    Actif
                                  </Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-700 border-0">
                                    <UserX className="h-3 w-3 mr-1" />
                                    Inactif
                                  </Badge>
                                )}
                                {user.access_restricted && (
                                  <Badge className="bg-red-100 text-red-700 border-0" data-testid={`restricted-badge-${user.id}`}>
                                    🚫 Bloqué
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {(user.role === 'housekeeper' ||
                                (user.role === 'technicien' && user.is_also_housekeeper)) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/admin/users/${user.id}/profil`)}
                                  title="Voir le profil (rejets et contrôles)"
                                  data-testid={`view-profile-${user.id}`}
                                >
                                  <Eye className="h-4 w-4 text-[#1F3A8A]" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" onClick={() => openEdit(user)} data-testid={`edit-user-${user.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              {(user.role === 'housekeeper' ||
                                (user.role === 'technicien' && user.is_also_housekeeper)) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setAdvancesTargetUser(user);
                                    setShowAdvancesDialog(true);
                                  }}
                                  title="Gérer les avances"
                                  data-testid={`advances-user-${user.id}`}
                                >
                                  <Wallet className="h-4 w-4 text-[#1F3A8A]" />
                                </Button>
                              )}
                              {user.is_active === false ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRestore(user)}
                                  className="text-emerald-700 hover:bg-emerald-50"
                                  data-testid={`restore-user-${user.id}`}
                                >
                                  ♻️ Restaurer
                                </Button>
                              ) : (
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Avatar Section - Only for editing */}
            {editingUser && (
              <div className="flex flex-col items-center pb-4 border-b">
                <div className="relative group">
                  <UserAvatar user={editingUser} size="lg" />
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    data-testid="change-avatar-btn"
                  >
                    {uploadingAvatar ? (
                      <Loader2 className="h-5 w-5 text-white animate-spin" />
                    ) : (
                      <Camera className="h-5 w-5 text-white" />
                    )}
                  </button>
                </div>
                <input
                  type="file"
                  ref={avatarInputRef}
                  onChange={handleAvatarUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="mt-2 text-sm text-[#003366] hover:underline"
                >
                  {uploadingAvatar ? 'Envoi en cours...' : 'Changer la photo'}
                </button>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Nom complet *</Label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Marie Dupont"
                data-testid="user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input 
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="marie@example.com"
                data-testid="user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>{editingUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}</Label>
              <Input 
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                data-testid="user-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input 
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="06 12 34 56 78"
                data-testid="user-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Rôle *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v, is_also_housekeeper: false })}>
                <SelectTrigger data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="technicien">Technicien</SelectItem>
                  <SelectItem value="housekeeper">Femme de ménage</SelectItem>
                  <SelectItem value="gouvernant">Gouvernant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Option for technician to also be a housekeeper */}
            {formData.role === 'technicien' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_also_housekeeper}
                    onChange={(e) => setFormData({ ...formData, is_also_housekeeper: e.target.checked })}
                    className="w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                    data-testid="is-also-housekeeper"
                  />
                  <div>
                    <span className="font-medium text-blue-800">Également femme de ménage</span>
                    <p className="text-xs text-blue-600">Ce technicien peut aussi recevoir et effectuer des missions de ménage</p>
                  </div>
                </label>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Secteur</Label>
              <Input 
                value={formData.sector}
                onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                placeholder="Quimper, Finistère..."
                data-testid="user-sector"
              />
            </div>
            
            {/* Auto-entrepreneur fields - for housekeepers and technicians who also do housekeeping */}
            {(formData.role === 'housekeeper' || (formData.role === 'technicien' && formData.is_also_housekeeper)) && (
              <>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-[#003366] mb-3 flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Informations auto-entrepreneur
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Numéro SIRET</Label>
                  <Input 
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    placeholder="123 456 789 00012"
                    data-testid="user-siret"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom de la société / Raison sociale</Label>
                  <Input 
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Marie Dupont EI"
                    data-testid="user-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresse de la société</Label>
                  <Input 
                    value={formData.company_address}
                    onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                    placeholder="12 rue des Lilas, 29000 Quimper"
                    data-testid="user-company-address"
                  />
                </div>
                
                {/* Option photos différées */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.allow_late_photos}
                      onChange={(e) => setFormData({ ...formData, allow_late_photos: e.target.checked })}
                      className="w-5 h-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                      data-testid="allow-late-photos"
                    />
                    <div>
                      <span className="font-medium text-amber-800">Photos différées autorisées</span>
                      <p className="text-xs text-amber-600">Peut valider une prestation sans photos et les ajouter plus tard. Peut aussi choisir l'heure de fin (10h-16h).</p>
                    </div>
                  </label>
                </div>
                
                {/* Option GPS obligatoire */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.require_gps_photos}
                      onChange={(e) => setFormData({ ...formData, require_gps_photos: e.target.checked })}
                      className="w-5 h-5 rounded border-red-300 text-red-600 focus:ring-red-500"
                      data-testid="require-gps-photos"
                    />
                    <div>
                      <span className="font-medium text-red-800">📍 Exiger GPS pour les photos</span>
                      <p className="text-xs text-red-600">Les photos doivent être prises à moins de 100m du logement. Empêche les photos prises ailleurs.</p>
                    </div>
                  </label>
                </div>

                {/* Option facturation au temps réel (32€/h) */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.enforce_time_billing}
                      onChange={(e) => setFormData({ ...formData, enforce_time_billing: e.target.checked })}
                      className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                      data-testid="enforce-time-billing"
                    />
                    <div>
                      <span className="font-medium text-purple-800">⏱️ Facturation au temps réel (32€/h)</span>
                      <p className="text-xs text-purple-600">Si la mission est terminée en avance sur le temps prévu (prix ÷ 32€), un dialog propose à la FDM de rester continuer ou de valider "j'ai vraiment fini" — auquel cas la mission est facturée au temps réel × 32€/h (plancher 80% du prix initial). Tolérance : 20% du temps prévu (donne plus de marge sur les longues missions).</p>
                    </div>
                  </label>

                  {/* Bouton bulk recalc — visible uniquement en édition */}
                  {editingUser && formData.enforce_time_billing && (
                    <div className="mt-3 pt-3 border-t border-purple-200 space-y-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleRecalculateTimeBilling}
                        disabled={recalculating}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm"
                        data-testid="recalculate-time-billing-btn"
                      >
                        {recalculating
                          ? '⏳ Recalcul en cours…'
                          : "🔄 Recalculer les missions terminées aujourd'hui au temps réel"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={runDiagnostic}
                        className="w-full text-purple-700 border-purple-300 hover:bg-purple-100 text-sm"
                        data-testid="diagnose-time-billing-btn"
                      >
                        🔎 Diagnostic — Voir les missions du jour et pourquoi elles sont/ne sont pas ajustées
                      </Button>
                      <p className="text-xs text-purple-500 mt-2 italic">
                        À utiliser si la case a été cochée après que la FDM ait terminé, ou si aucun dialog ne s'est déclenché sur ses missions du jour.
                      </p>
                    </div>
                  )}

                  {/* Diagnostic result */}
                  {showDiagnostic && diagnostic && !diagnostic.loading && !diagnostic.error && (
                    <div className="mt-3 pt-3 border-t border-purple-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-purple-700">
                          🔎 Diagnostic du {diagnostic.date}
                        </p>
                        <button type="button" className="text-xs text-purple-500 underline" onClick={() => setShowDiagnostic(false)}>Fermer</button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div className="bg-white border border-purple-100 rounded p-2">Missions totales : <strong>{diagnostic.total_tasks}</strong></div>
                        <div className="bg-white border border-purple-100 rounded p-2">Ajustables : <strong className="text-green-700">{diagnostic.eligible_for_adjustment}</strong></div>
                        <div className="bg-white border border-purple-100 rounded p-2">Déjà ajustées : <strong className="text-blue-700">{diagnostic.already_adjusted}</strong></div>
                        <div className="bg-white border border-purple-100 rounded p-2">Sans GPS check-in : <strong className="text-red-700">{diagnostic.no_gps_checkin}</strong></div>
                      </div>
                      <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="diagnostic-list">
                        {diagnostic.tasks.length === 0 && (
                          <p className="text-xs text-slate-500 italic p-2">Aucune mission ce jour-là.</p>
                        )}
                        {diagnostic.tasks.map((row) => (
                          <div key={row.task_id} className="text-xs bg-white border border-purple-100 rounded-lg p-2">
                            <div className="font-medium text-slate-800">
                              {row.property_name || 'Logement'} · {row.price}€
                              {row.original_price && row.original_price !== row.price && (
                                <span className="text-slate-400 ml-1">(orig. {row.original_price}€)</span>
                              )}
                            </div>
                            <div className="text-slate-600 mt-0.5">{row.reason}</div>
                            {row.actual_minutes != null && (
                              <div className="text-slate-500 mt-0.5">
                                Temps réel : {row.actual_minutes} min · prévu : {row.expected_minutes} min
                                {row.would_adjust_to != null && ` · ajustement → ${row.would_adjust_to}€`}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {showDiagnostic && diagnostic?.loading && (
                    <p className="text-xs text-purple-500 mt-3 italic">Chargement diagnostic…</p>
                  )}
                  {showDiagnostic && diagnostic?.error && (
                    <p className="text-xs text-red-600 mt-3">Erreur : {diagnostic.error}</p>
                  )}

                  {/* Historique des ajustements */}
                  {editingUser && adjustmentHistory && adjustmentHistory.total > 0 && (
                    <div className="mt-4 pt-3 border-t border-purple-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-purple-700">
                          Historique des ajustements
                        </p>
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                          {adjustmentHistory.total_saved_eur}€ économisés au total
                        </span>
                      </div>
                      <div className="space-y-1.5 max-h-52 overflow-y-auto" data-testid="adjustment-history-list">
                        {adjustmentHistory.items.map((adj) => (
                          <div key={adj.task_id} className="flex justify-between items-start text-xs bg-white border border-purple-100 rounded-lg p-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-800 truncate">
                                {adj.task_date} · {adj.property_name || 'Logement'}
                              </div>
                              <div className="text-slate-500">
                                {adj.actual_minutes}min au lieu de {adj.expected_minutes}min
                                {adj.confirmed_by_admin && ' · ajusté par admin'}
                              </div>
                            </div>
                            <div className="text-right ml-2">
                              <div className="line-through text-slate-400">{adj.original_price}€</div>
                              <div className="font-bold text-purple-700">{adj.adjusted_price}€</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {editingUser && loadingAdjustments && (
                    <p className="text-xs text-purple-500 mt-3 italic">Chargement historique…</p>
                  )}
                </div>

                {/* Option skip photo boîte à clés */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.skip_keybox_photo}
                      onChange={(e) => setFormData({ ...formData, skip_keybox_photo: e.target.checked })}
                      className="w-5 h-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                      data-testid="skip-keybox-photo"
                    />
                    <div>
                      <span className="font-medium text-amber-800">🔑 Ne pas demander la photo de la boîte à clés</span>
                      <p className="text-xs text-amber-600">La photo de la boîte à clés ne sera pas obligatoire pour terminer la mission. À utiliser pour les FDM qui rencontrent des bugs répétés lors de cette étape (téléphone incompatible, caméra qui plante, etc.).</p>
                    </div>
                  </label>
                </div>

                {/* Option accès depuis l'étranger (whitelist géographique) */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.allow_foreign_access}
                      onChange={(e) => setFormData({ ...formData, allow_foreign_access: e.target.checked })}
                      className="w-5 h-5 rounded border-red-300 text-red-600 focus:ring-red-500"
                      data-testid="allow-foreign-access"
                    />
                    <div>
                      <span className="font-medium text-red-800">🌍 Autoriser la connexion depuis l'étranger</span>
                      <p className="text-xs text-red-600">Par défaut, l'application est bloquée hors de France (métropole + DOM-TOM). Cocher cette case si cette FDM voyage ou utilise un forfait mobile qui simule une IP étrangère.</p>
                    </div>
                  </label>
                </div>

                {/* Option notifications emails */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.email_notifications_enabled}
                      onChange={(e) => setFormData({ ...formData, email_notifications_enabled: e.target.checked })}
                      className="w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                      data-testid="email-notifications-enabled"
                    />
                    <div>
                      <span className="font-medium text-blue-800">✉️ Recevoir les notifications par email</span>
                      <p className="text-xs text-blue-600">Si décoché, cette FDM ne recevra plus les emails d'assignation, d'annulation ou de modification de mission.</p>
                    </div>
                  </label>
                </div>

                {/* Option linge (auto split sur les prestations en équipe avec linge) */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mt-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.does_not_do_linen}
                      onChange={(e) => setFormData({ ...formData, does_not_do_linen: e.target.checked })}
                      className="w-5 h-5 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                      data-testid="does-not-do-linen"
                    />
                    <div>
                      <span className="font-medium text-indigo-800">👕 Ne s'occupe pas du linge en équipe</span>
                      <p className="text-xs text-indigo-700">Sur les prestations en binôme avec linge, sa part sera automatiquement réduite et l'autre FDM recevra le reste.</p>
                    </div>
                  </label>
                  {formData.does_not_do_linen && (
                    <div className="mt-3 pl-8">
                      <Label className="text-xs text-indigo-800 font-semibold block mb-1">
                        Pourcentage qu'elle touche sur les prestations en équipe avec linge :
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={formData.linen_personal_percentage}
                          onChange={(e) => setFormData({ ...formData, linen_personal_percentage: Number(e.target.value) })}
                          className="w-24 px-2 py-1 text-sm border border-indigo-300 rounded-md bg-white"
                          data-testid="linen-personal-percentage"
                        />
                        <span className="text-indigo-700 font-semibold">%</span>
                        <span className="text-xs text-slate-500">(défaut conseillé : 30%)</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Option accès restreint */}
                <div className={`border rounded-lg p-3 mt-3 ${formData.access_restricted ? 'bg-red-100 border-red-400' : 'bg-red-50 border-red-200'}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.access_restricted}
                      onChange={(e) => setFormData({ ...formData, access_restricted: e.target.checked })}
                      className="w-5 h-5 rounded border-red-400 text-red-700 focus:ring-red-600"
                      data-testid="access-restricted-toggle"
                    />
                    <div>
                      <span className="font-bold text-red-900">🚫 Restreindre l'accès à l'application</span>
                      <p className="text-xs text-red-700">À cocher si des prestations assignées n'ont pas été réalisées. La FDM pourra se connecter mais verra un écran de blocage l'invitant à contacter l'administrateur.</p>
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button className="bg-[#003366] hover:bg-[#002244]" onClick={handleSubmit} disabled={actionLoading} data-testid="save-user-btn">
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingUser ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-[#003366]" />
              Historique des connexions (7 derniers jours)
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {loginHistory?.users?.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Aucune connexion enregistrée sur cette période</p>
            ) : (
              <div className="space-y-4">
                {loginHistory?.users?.map((userHistory) => (
                  <div key={userHistory.user_id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-slate-800">{userHistory.user_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {userHistory.logins.length} connexion{userHistory.logins.length > 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {userHistory.logins.slice(0, 10).map((login, idx) => (
                        <span 
                          key={login.timestamp || `login-${idx}`} 
                          className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded"
                          title={new Date(login.timestamp).toLocaleString('fr-FR')}
                        >
                          {new Date(login.timestamp).toLocaleDateString('fr-FR', { 
                            weekday: 'short', 
                            day: '2-digit', 
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      ))}
                      {userHistory.logins.length > 10 && (
                        <span className="text-xs text-slate-400">+{userHistory.logins.length - 10} autres</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Logs Dialog */}
      <Dialog open={showEmailLogsDialog} onOpenChange={setShowEmailLogsDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-600" />
              Historique des emails envoyés
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {/* Quick test buttons per FDM */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Send className="h-4 w-4" />
                Envoyer un email de test
              </h4>
              <div className="flex flex-wrap gap-2">
                {users.filter(u => u.role === 'housekeeper' || u.role === 'technicien').map(user => (
                  <Button
                    key={user.id}
                    variant="outline"
                    size="sm"
                    disabled={sendingTestEmail === user.id}
                    onClick={() => sendTestEmailToFDM(user.id, user.name, user.email)}
                    className="text-xs"
                  >
                    {sendingTestEmail === user.id ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Send className="h-3 w-3 mr-1" />
                    )}
                    {user.name?.split(' ')[0] || user.email}
                  </Button>
                ))}
              </div>
            </div>

            {/* Email logs list */}
            {emailLogs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Mail className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>Aucun email envoyé pour le moment</p>
              </div>
            ) : (
              <div className="space-y-3">
                {emailLogs.map((log, idx) => (
                  <div 
                    key={log.id || idx} 
                    className={`border rounded-lg p-4 ${
                      log.status === 'sent' ? 'border-emerald-200 bg-emerald-50/50' :
                      log.status === 'failed' ? 'border-red-200 bg-red-50/50' :
                      'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {log.status === 'sent' ? (
                            <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          ) : log.status === 'failed' ? (
                            <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                          ) : (
                            <Clock className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          )}
                          <span className="font-medium text-slate-800 truncate">{log.subject}</span>
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p className="flex items-center gap-2">
                            <Mail className="h-3 w-3 text-slate-400" />
                            <span className="font-mono text-xs">{log.to_email}</span>
                            {log.context?.housekeeper_name && (
                              <Badge variant="outline" className="text-xs">
                                {log.context.housekeeper_name}
                              </Badge>
                            )}
                          </p>
                          {log.context?.type && (
                            <p className="flex items-center gap-2">
                              <Badge className={`text-xs ${
                                log.context.type === 'assignment' ? 'bg-blue-100 text-blue-700' :
                                log.context.type === 'cancellation' ? 'bg-red-100 text-red-700' :
                                log.context.type === 'test' ? 'bg-purple-100 text-purple-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {log.context.type === 'assignment' ? 'Assignation' :
                                 log.context.type === 'cancellation' ? 'Annulation' :
                                 log.context.type === 'test' ? 'Test' : log.context.type}
                              </Badge>
                              {log.context.property_name && (
                                <span className="text-xs text-slate-500">{log.context.property_name}</span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-slate-500">
                          {new Date(log.sent_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {log.resend_id && (
                          <p className="text-[10px] text-slate-400 font-mono mt-1">
                            ID: {log.resend_id.slice(0, 8)}...
                          </p>
                        )}
                        {log.error && (
                          <p className="text-xs text-red-500 mt-1">{log.error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 border-t flex justify-between items-center">
            <p className="text-xs text-slate-500">
              {emailLogs.length} email{emailLogs.length > 1 ? 's' : ''} dans l'historique
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchEmailLogs}
              disabled={emailLogsLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${emailLogsLoading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advances Dialog */}
      <AdvancesDialog
        open={showAdvancesDialog}
        onOpenChange={setShowAdvancesDialog}
        housekeeper={advancesTargetUser}
      />
    </div>
  );
};

export default UsersManagement;
